import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BankAccountCreateInputDto /*, CreateInternationalBankAccountInputDto, Currency */ } from 'src/app/core/data-models/payment.data.models';
import { PortalBankAccount } from 'src/app/core/data-models/payment.data.models';
import { PaymentDataService } from 'src/app/core/data-services/payment/payment-data.service';
import { /* ibanValidator, */ saIbanValidator /*, swiftCodeValidator */ } from '../../shared/jari-validators';
import { NotificationService } from 'src/app/core/common-services/notification.service';
import { LanguageService } from '../../services/language.service';
import { SharedModule } from '../../shared/shared.module';
import {
  readGeneralResponseMessage,
  readGeneralResponseSuccess,
  readHttpErrorApiMessage,
} from 'src/app/shared/utils/api-response-message.util';

@Component({
  selector: 'app-bank-account-form',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './bank-account-form.component.html',
  styleUrls: ['./bank-account-form.component.css']
})
export class BankAccountFormComponent implements OnInit {
  /** Saudi IBAN: SA + 22 digits (24 chars without spaces). */
  readonly saIbanRawMaxLength = 24;
  /** Formatted IBAN with spaces every 4 characters. */
  readonly saIbanFormattedMaxLength = 29;

  // activeTab = 0;

  currentLang = this.languageService.currentLanguage || 'ar';
  saudiForm!: FormGroup;
  // internationalForm!: FormGroup;
  banks: any[] = [];
  // uploadedFile!: File | null;
  // currencyArray: Currency[] = [];

  private hasExistingAccounts = false;

  /** "Set as primary account?" modal, asked before saving a 2nd+ account. Closing it saves nothing. */
  primaryPromptVisible = false;
  saving = false;

  constructor(
    private fb: FormBuilder,
    private paymentService: PaymentDataService,
    private router: Router,
    private route: ActivatedRoute,
    private languageService: LanguageService,
    private notificationService: NotificationService,
    private translate: TranslateService,
  ) { }

  ngOnInit(): void {

    this.currentLang = this.languageService.currentLanguage;
    // this.loadCurrency();
    this.buildForm();
    this.loadBanks();
    this.checkHasExistingAccounts();
  }

  private checkHasExistingAccounts(): void {
    this.paymentService.GetBankAccountsByUser().subscribe({
      next: (res) => {
        const items: PortalBankAccount[] = res?.resource?.items || [];
        this.hasExistingAccounts = items.length > 0;
      }
    });
  }

  buildForm(): void {
    this.saudiForm = this.fb.group({
      bankId: ['', Validators.required],
      iban: ['', [Validators.required, saIbanValidator]],
      accountName: ['', Validators.required]
    });
    /*
   this.internationalForm = this.fb.group({
  bankName: ['', Validators.required],
  iban: ['', [Validators.required, ibanValidator]],
  accountName: ['', Validators.required],
  swiftCode: ['', [Validators.required, swiftCodeValidator]],
  currencyId: [null, Validators.required],
  attachment: [null, Validators.required],
  bankAddress: ['']
});
    */
  }


  loadBanks(): void {
    this.paymentService.GetAllBanks().subscribe({
      next: (res) => this.banks = res?.resource || []
    });
  }
    /*
    loadCurrency(): void {
    this.paymentService.GetAllCurrency().subscribe({
      next: (res) => this.currencyArray = res?.resource || []
    });
  }


  onFileSelected(event: any) {
  const file = event.target.files[0];
  if (file) {
    this.internationalForm.patchValue({
      attachment: file
    });
    this.internationalForm.get('attachment')?.updateValueAndValidity();
  }
}
    */


  /** True when the typed IBAN does not start with "SA" (a lone "S" still counts as a valid start). */
  get ibanMissingSaPrefix(): boolean {
    const value = (this.saudiForm.get('iban')?.value || '').replace(/\s+/g, '').toUpperCase();
    return value.length > 0 && !'SA'.startsWith(value.slice(0, 2));
  }

  /**
   * AC: adding a 2nd+ bank account asks "Set as Primary Account?". The question is asked before
   * anything is saved, so closing the modal discards the entry instead of leaving a saved account.
   */
  submitSaudi(): void {
    if (this.saudiForm.invalid) {
      this.saudiForm.markAllAsTouched();
      return;
    }
    if (this.saving) {
      return;
    }

    if (this.hasExistingAccounts) {
      this.primaryPromptVisible = true;
      return;
    }

    this.createSaudiAccount(false);
  }

  answerPrimaryPrompt(setAsPrimary: boolean): void {
    this.primaryPromptVisible = false;
    this.createSaudiAccount(setAsPrimary);
  }

  private createSaudiAccount(setAsPrimary: boolean): void {
    const iban = (this.saudiForm.value.iban || '').replace(/\s+/g, '').toUpperCase();
    const dto: BankAccountCreateInputDto = {
      bankId: this.saudiForm.value.bankId,
      iban,
      accountName: this.saudiForm.value.accountName
    };

    this.saving = true;
    this.paymentService.CreateBankAccount(dto).subscribe({
      next: (res) => {
        if (readGeneralResponseSuccess(res) === false) {
          this.saving = false;
          this.showCreateError(readGeneralResponseMessage(res));
          return;
        }
        this.notificationService.success('BankAccount.CREATE_SUCCESS');
        if (setAsPrimary) {
          this.setCreatedAccountAsPrimary(iban);
        } else {
          this.back();
        }
      },
      error: (err) => {
        this.saving = false;
        this.showCreateError(readHttpErrorApiMessage(err));
      }
    });
  }

  /** The create response carries no id, so the new account is found by its IBAN. */
  private setCreatedAccountAsPrimary(createdIban: string): void {
    this.paymentService.GetBankAccountsByUser().subscribe({
      next: (res) => {
        const items: PortalBankAccount[] = res?.resource?.items || [];
        const created = items.find((a) => (a.iban || '').replace(/\s+/g, '').toUpperCase() === createdIban);
        if (!created) {
          this.back();
          return;
        }
        this.paymentService.SetAsPrimary(created.id).subscribe({
          next: () => this.back(),
          error: () => {
            this.notificationService.error('BankAccount.SET_PRIMARY_FAILED');
            this.back();
          },
        });
      },
      error: () => this.back(),
    });
  }

  private showCreateError(message: string): void {
    const text = (message || '').trim() || this.translate.instant('BankAccount.CREATE_FAILED');
    this.notificationService.errorLiteral(text);
  }
/*
submitInternational(): void {
  if (this.internationalForm.invalid) {
    this.internationalForm.markAllAsTouched();
    return;
  }

  const value = this.internationalForm.value;

const body: CreateInternationalBankAccountInputDto = {
  bankName: value.bankName,
  iban: value.iban.replace(/\s+/g, ''),
  accountName: value.accountName,
  swiftCode: value.swiftCode.toUpperCase(),
  currencyId: value.currencyId,
  attachment: value.attachmentPath,
  BankAddress: value.BankAddress || ''
};

this.paymentService.CreateInternationalBankAccount(body).subscribe({
    next: () => this.back(),
    error: (err) => console.error('Error creating account', err)
  });
}
*/



  back() {
    if (this.router.url.includes('/Investor/')) {
      this.router.navigate(['/Investor/Profile'], { fragment: 'bankAccounts' });
      return;
    }

    const role = this.route.snapshot.paramMap.get('role');
    if (role) {
      this.router.navigate([`/${role}/account-setting`]);
      return;
    }

    this.router.navigate(['/Investor/Profile'], { fragment: 'bankAccounts' });
  }

  formatIban(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\s+/g, '').toUpperCase();

    if (value.length > this.saIbanRawMaxLength) {
      value = value.slice(0, this.saIbanRawMaxLength);
    }

    const formatted = value.match(/.{1,4}/g)?.join(' ') || '';
    this.saudiForm.get('iban')?.setValue(formatted, { emitEvent: false });
    input.value = formatted;
  }

  /*
  activateTab(index: number) {
    this.activeTab = index
  }
  */
}
